import { useT, type MessageKey } from "../../i18n/index.js";
import { useRemountSafeState } from "../../state/remount-safe-state";
import { BADGE_TONE, type BadgeTone } from "../../ui/classes";

interface ToolCallCardProps {
  readonly toolUseId: string;
  readonly toolName?: string;
  readonly input?: unknown;
  readonly output?: unknown;
  readonly ok?: boolean;
}

const stringify = (v: unknown): string => {
  if (v === undefined) return "";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const STATUS_LABEL_KEY: Record<"running" | "succeeded" | "failed", MessageKey> = {
  running: "chatRender.statusRunning",
  succeeded: "chatRender.statusSucceeded",
  failed: "chatRender.statusFailed",
};

const STATUS_TONE: Record<"running" | "succeeded" | "failed", BadgeTone> = {
  running: "neutral",
  succeeded: "success",
  failed: "danger",
};

export function ToolCallCard({ toolUseId, toolName, input, output, ok }: ToolCallCardProps) {
  const t = useT();
  // Remount-safe (toolUseId is stable across the reconnect replay) so an
  // expanded card the user is reading doesn't snap shut on a socket flap.
  const [expanded, setExpanded] = useRemountSafeState(
    `toolcall:${toolUseId}:expanded`,
    false,
  );
  const status = ok === undefined ? "running" : ok ? "succeeded" : "failed";
  const badge = BADGE_TONE[STATUS_TONE[status]];

  return (
    <div className="rounded-lg border border-line bg-surface-2 p-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="font-mono text-xs text-ink-3">{toolName ?? t("chatRender.toolFallback")}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge}`}>
          {t(STATUS_LABEL_KEY[status])}
        </span>
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {input !== undefined && (
            <div>
              <div className="text-[10px] font-semibold uppercase text-ink-subtle">{t("chatRender.inputLabel")}</div>
              <pre className="overflow-x-auto rounded bg-surface p-2 text-xs">{stringify(input)}</pre>
            </div>
          )}
          {output !== undefined && (
            <div>
              <div className="text-[10px] font-semibold uppercase text-ink-subtle">{t("chatRender.outputLabel")}</div>
              <pre className="overflow-x-auto rounded bg-surface p-2 text-xs">
                {stringify(output)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
